import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The lesson text section being read, held in the URL -
 * `?tab=lesson&view=text&section=3` - so a link opens a section and the
 * browser's Back returns from a section to the contents list.
 *
 * Opening a section from the list PUSHES a history entry (Back returns to
 * the list); moving to the previous or next section REPLACES it (Back still
 * returns to the list, not through every section read). `null` is the list.
 */
export function useTextSectionParam(): IndexParam {
  return useIndexParam('section');
}

/** The section whose podcast is open on the Podcast tab -
 *  `?view=audio&episode=2`; `null` is the list of section podcasts. Same
 *  history rules as the text section. */
export function usePodcastSectionParam(): IndexParam {
  return useIndexParam('episode');
}

interface IndexParam {
  section: number | null;
  open: (index: number, how?: 'push' | 'replace') => void;
  close: () => void;
}

function useIndexParam(key: string): IndexParam {
  const [params, setParams] = useSearchParams();
  const raw = Number(params.get(key));
  const section = Number.isInteger(raw) && raw > 0 ? raw : null;

  const open = useCallback(
    (index: number, how: 'push' | 'replace' = 'push') => {
      setParams(
        prev => {
          const p = new URLSearchParams(prev);
          p.set(key, String(index));
          return p;
        },
        { replace: how === 'replace' }
      );
    },
    [setParams, key]
  );

  const close = useCallback(() => {
    setParams(
      prev => {
        const p = new URLSearchParams(prev);
        p.delete(key);
        return p;
      },
      { replace: true }
    );
  }, [setParams, key]);

  return { section, open, close };
}
